import { zodResolver } from '@hookform/resolvers/zod';
import { EGrammarType } from '@quesmed/types';
import { EContentType, EProductType } from '@quesmed/types/models';
import { useMemo, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { contentTypes, dbConfig, productMapping, quesProducts, templates } from '@/config/constants';
import { Heading } from '@/core/molecules';
import {
  Button,
  cn,
  FormCheckBox,
  FormSelect,
  type FormSelectProps,
  type ISelectItemSection,
  toaster,
} from '@/core/ui';
import { useGlobal } from '@/store';
import { localFileDownload } from '@/utils/dom';

const schema = z.object({
  productType: z.enum(EProductType, { message: 'Invalid Product value' }),
  contentType: z.enum(EContentType, { message: 'Invalid Content value' }),
  grammarType: z.enum(EGrammarType, { message: 'Invalid Grammar value' }),
  images: z.boolean(),
});

type ISchema = z.infer<typeof schema>;

const Templates = () => {
  const dbType = useGlobal.use.dbType();
  const [grammarTypes, setGrammarTypes] = useState<ISelectItemSection[]>([]);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<ISchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      images: false,
    },
  });

  const onSubmit: SubmitHandler<ISchema> = async (input) => {
    const contents = templates[input.grammarType];
    if (!contents) {
      toaster.info('We dont have template for this content yet!');
      return;
    }
    const path = contents[input.contentType];
    if (!path) {
      toaster.info('We dont have template for this content yet!');
      return;
    }

    const suffix = input.images ? '-images' : '';

    localFileDownload(
      `TEMPLATE_${EGrammarType[input.grammarType]}_${EContentType[input.contentType]}_${suffix.toUpperCase()}`,
      `${path}${suffix}.zip`,
    );
    return path;
  };

  const handleProductChange: FormSelectProps<ISchema>['onValueChange'] = (value) => {
    const grammarTypes = value ? productMapping[value as EProductType]?.grammarTypes : null;
    if (!grammarTypes) {
      return;
    }
    setGrammarTypes(grammarTypes);
  };

  const formData = useMemo(() => {
    return {
      products: quesProducts.filter((p) => dbConfig[dbType]?.products?.includes(p.key as EProductType)),
    };
  }, [dbType]);

  return (
    <div className='flex flex-col space-y-2 justify-center w-full items-center'>
      <div className='bg-background rounded-lg shadow-md w-full container z-[2]'>
        <Heading title='Download Content Templates' />

        <form onSubmit={handleSubmit(onSubmit)} className='p-6 md:p-10'>
          <div className='grid grid-cols-3 items-start gap-y-6 md:gap-y-10 gap-x-8'>
            <FormSelect
              isRequired={true}
              description='Select category for product'
              options={formData.products}
              // description="This is basically type of product category"
              control={control}
              onValueChange={handleProductChange}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              name='productType'
              placeholder='Select Product Category'
              className='rounded col-span-3 md:col-span-1'
              variant='faded'
              label='Product Category'
            />
            <FormSelect
              isRequired={true}
              description=' '
              sections={grammarTypes}
              options={[]}
              control={control}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              name='grammarType'
              placeholder='Select Grammar Type'
              className='rounded col-span-3 md:col-span-1'
              variant='faded'
              label='Grammar Type'
            />
            <FormSelect
              isRequired={true}
              description=' '
              options={contentTypes}
              // description="This is basically type of question"
              control={control}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              placeholder='Select Input Content Type'
              name='contentType'
              className='rounded col-span-3 md:col-span-1'
              variant='faded'
              label='Content Type'
            />
            <div
              className={cn(
                'flex flex-col justify-between bg-default-100 rounded-lg h-full',
                'border-2 shadow-sm border-default-200 px-4 py-1',
              )}
            >
              <FormCheckBox
                classNames={{ label: 'text-sm' }}
                className='py-4'
                control={control}
                name='images'
              >
                <span className='text-foreground-500 text-small'>Template with images</span>
              </FormCheckBox>
            </div>
          </div>
          <div className='flex justify-center mt-10'>
            <Button
              isDisabled={!isValid}
              className='w-full max-w-xl bg-primary'
              type='submit'
              radius='sm'
              size='lg'
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Templates;
